// Internal DriverWatch Admin Portal - Staff Management
// Manage internal DriverWatch staff accounts and roles

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import config from "../config";

const API_URL = config.API_URL;

export default function StaffManagement() {
  const storedUser = JSON.parse(sessionStorage.getItem("internal_user") || "{}");
  const navigate = useNavigate();
  const token = sessionStorage.getItem("internal_token");

  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [formError, setFormError] = useState(null);
  const [formSuccess, setFormSuccess] = useState(null);

  const [newStaff, setNewStaff] = useState({
    full_name: "",
    email: "",
    password: "",
    role: "developer"
  });

  function fetchStaff() {
    axios
      .get(`${API_URL}/internal/users`, {
        headers: { Authorization: `Bearer ${sessionStorage.getItem("internal_token")}` }
      })
      .then((res) => {
        setStaff(res.data);
        setLoading(false);
      })
      .catch(() => {
        setError("Failed to load staff");
        setLoading(false);
      });
  }

  useEffect(() => {
    if (!token) { navigate("/login"); return; }
    fetchStaff();
  }, []);

  async function handleAddStaff(e) {
    e.preventDefault();
    setFormError(null);
    setFormSuccess(null);

    try {
      await axios.post(
        `${API_URL}/internal/users?email=${encodeURIComponent(newStaff.email)}&password=${encodeURIComponent(newStaff.password)}&full_name=${encodeURIComponent(newStaff.full_name)}&role=${newStaff.role}`,
        {},
        { headers: { Authorization: `Bearer ${sessionStorage.getItem("internal_token")}` } }
      );
      setFormSuccess(`${newStaff.full_name} added successfully`);
      setNewStaff({ full_name: "", email: "", password: "", role: "developer" });
      setShowAddForm(false);
      fetchStaff();
    } catch (err) {
      setFormError(err.response?.data?.detail || "Failed to add staff member");
    }
  }

  async function handleDeleteStaff(userId, userEmail) {
    if (!window.confirm(`Delete staff member ${userEmail}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API_URL}/internal/users/${userId}`, {
        headers: { Authorization: `Bearer ${sessionStorage.getItem("internal_token")}` }
      });
      fetchStaff();
    } catch (err) {
      setError("Failed to delete staff member");
    }
  }

  async function handleRoleChange(userId, newRole) {
    try {
      await axios.put(`${API_URL}/internal/users/${userId}/role?role=${newRole}`, {}, {
        headers: { Authorization: `Bearer ${sessionStorage.getItem("internal_token")}` }
      });
      fetchStaff();
    } catch (err) {
      setError("Failed to update role");
    }
  }

  if (loading) return (
    <div className="flex items-center justify-center h-screen bg-gray-950">
      <p className="text-white text-xl">Loading staff...</p>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-950 text-white">

      {/* Navigation bar */}
      <nav className="bg-gray-900 border-b border-red-900 px-8 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <img src="/logo-symbol.svg" alt="AleroSys" className="h-8 w-8" />
          <div className="flex items-center gap-2">
            <span className="text-white font-bold text-lg">AleroSys</span>
            <span className="text-gray-500 text-lg font-light">—</span>
            <span className="text-red-400 text-lg font-semibold">DriverWatch Admin</span>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-gray-400 text-sm">{storedUser.full_name} — {storedUser.role}</span>
          <button
            onClick={() => navigate("/")}
            className="text-gray-400 hover:text-white text-sm transition-colors"
          >
            ← Back to Dashboard
          </button>
          <button
            onClick={() => {
              sessionStorage.removeItem("internal_token");
              sessionStorage.removeItem("internal_user");
              window.location.href = "/login";
            }}
            className="text-gray-400 hover:text-white text-sm border border-gray-700 hover:border-gray-500 px-3 py-1 rounded-lg transition-colors"
          >
            Sign out
          </button>
        </div>
      </nav>

      {/* Main content */}
      <main className="px-8 py-8">

        {/* Page title */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-white">Staff Management</h1>
            <p className="text-gray-400 mt-1">Manage internal DriverWatch staff accounts</p>
          </div>
          <button
            onClick={() => {
              setShowAddForm(!showAddForm);
              setFormError(null);
              setFormSuccess(null);
            }}
            className="bg-red-600 hover:bg-red-700 text-white text-sm px-4 py-2 rounded-lg transition-colors"
          >
            {showAddForm ? "Cancel" : "+ Add Staff"}
          </button>
        </div>

        {/* Success message */}
        {formSuccess && (
          <div className="bg-green-900 border border-green-700 text-green-300 px-4 py-3 rounded-lg mb-6 text-sm">
            {formSuccess}
          </div>
        )}

        {/* Error message */}
        {error && (
          <div className="bg-red-900 border border-red-700 text-red-300 px-4 py-3 rounded-lg mb-6 text-sm">
            {error}
          </div>
        )}

        {/* Add Staff form */}
        {showAddForm && (
          <div className="bg-gray-900 rounded-xl p-6 border border-red-800 mb-8">
            <h2 className="text-white font-semibold mb-4">Add New Staff Member</h2>
            {formError && (
              <div className="bg-red-900 border border-red-700 text-red-300 px-4 py-3 rounded-lg mb-4 text-sm">
                {formError}
              </div>
            )}
            <form onSubmit={handleAddStaff}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-gray-400 text-sm mb-2">Full Name</label>
                  <input
                    type="text"
                    value={newStaff.full_name}
                    onChange={(e) => setNewStaff({ ...newStaff, full_name: e.target.value })}
                    placeholder="John Smith"
                    className="w-full bg-gray-800 border border-gray-700 text-white rounded-lg px-4 py-2 text-sm focus:outline-none focus:border-red-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-gray-400 text-sm mb-2">Email Address</label>
                  <input
                    type="email"
                    value={newStaff.email}
                    onChange={(e) => setNewStaff({ ...newStaff, email: e.target.value })}
                    placeholder="john@alerosys.com"
                    className="w-full bg-gray-800 border border-gray-700 text-white rounded-lg px-4 py-2 text-sm focus:outline-none focus:border-red-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-gray-400 text-sm mb-2">Password</label>
                  <input
                    type="password"
                    value={newStaff.password}
                    onChange={(e) => setNewStaff({ ...newStaff, password: e.target.value })}
                    placeholder="••••••••"
                    className="w-full bg-gray-800 border border-gray-700 text-white rounded-lg px-4 py-2 text-sm focus:outline-none focus:border-red-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-gray-400 text-sm mb-2">Role</label>
                  <select
                    value={newStaff.role}
                    onChange={(e) => setNewStaff({ ...newStaff, role: e.target.value })}
                    className="w-full bg-gray-800 border border-gray-700 text-white rounded-lg px-4 py-2 text-sm focus:outline-none focus:border-red-500"
                  >
                    <option value="developer">Developer</option>
                    <option value="admin">Admin</option>
                    <option value="billing">Billing</option>
                    <option value="owner">Owner</option>
                  </select>
                </div>
              </div>
              <button
                type="submit"
                className="bg-red-600 hover:bg-red-700 text-white text-sm px-6 py-2 rounded-lg transition-colors"
              >
                Add Staff Member
              </button>
            </form>
          </div>
        )}

        {/* Staff table */}
        <div className="bg-gray-900 rounded-xl p-6 border border-gray-800">
          <h2 className="text-white font-semibold mb-4">Staff ({staff.length})</h2>
          {staff.length === 0 ? (
            <p className="text-gray-400">No staff found</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-gray-400 border-b border-gray-800">
                  <th className="text-left py-3">Name</th>
                  <th className="text-left py-3">Email</th>
                  <th className="text-left py-3">Role</th>
                  <th className="text-left py-3">Added</th>
                  <th className="text-left py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {staff.map((member) => (
                  <tr key={member.id} className="border-b border-gray-800">
                    <td className="py-3 text-white font-medium">{member.full_name}</td>
                    <td className="py-3 text-gray-400">{member.email}</td>
                    <td className="py-3">
                      {member.email === storedUser.email ? (
                        <span className="bg-red-900 text-red-300 text-xs px-2 py-1 rounded-full">
                          {member.role} (you)
                        </span>
                      ) : (
                        <select
                          value={member.role}
                          onChange={(e) => handleRoleChange(member.id, e.target.value)}
                          className="bg-gray-800 border border-gray-700 text-white rounded px-2 py-1 text-xs focus:outline-none"
                        >
                          <option value="developer">Developer</option>
                          <option value="admin">Admin</option>
                          <option value="billing">Billing</option>
                          <option value="owner">Owner</option>
                        </select>
                      )}
                    </td>
                    <td className="py-3 text-gray-400">
                      {new Date(member.created_at).toLocaleDateString()}
                    </td>
                    <td className="py-3">
                      {member.email !== storedUser.email && (
                        <button
                          onClick={() => handleDeleteStaff(member.id, member.email)}
                          className="text-red-400 hover:text-red-300 text-xs border border-red-800 hover:border-red-600 px-2 py-1 rounded transition-colors"
                        >
                          Delete
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

      </main>
    </div>
  );
}